import React, { useEffect } from 'react';
import styled from 'styled-components';

import useCart from 'hooks/use-cart';
import { colors } from 'shared/styles';
import type { RewardV2 } from 'src/gql/graphql';
import Button from '../../core/button';

type RewardsProps = {
  rewards: Array<RewardV2>;
  onApplyReward?: (copy: string, id: string) => void;
  onUndoReward?: (id: string) => void;
  rewardsBalanceNumber: number;
  showDiscountRewardsOnly: boolean;
  maxRedemptionsPerTransaction: number | null;
  showRedemptionLimit: boolean;
  /** Blocks reward application; defaults to `showDiscountRewardsOnly`. */
  isApplyBlocked?: boolean;
};

// Shared by the click handler and render-time disabled state so reward eligibility cannot diverge.
export function canApplyReward({
  isApplyBlocked,
  showDiscountRewardsOnly,
  isRedeemableOnline,
  isEligibleForReward,
  allowStacking,
  hasCartDiscount = false,
}: {
  isApplyBlocked: boolean;
  showDiscountRewardsOnly: boolean;
  isRedeemableOnline?: boolean | null;
  isEligibleForReward: boolean;
  /** POS stacking flag for the reward's backing discount; null/undefined means unknown (never blocks). */
  allowStacking?: boolean | null;
  /** Whether the cart already carries a discount a non-stacking reward must not combine with. */
  hasCartDiscount?: boolean;
}): boolean {
  // A reward whose POS discount is configured non-stacking cannot be applied on top of an
  // existing automatic discount or promo code (ECOMM-11514). An absent flag never blocks so
  // older payloads (no Armageddon enrichment) keep today's behavior.
  const stackingBlocked = hasCartDiscount && allowStacking === false;
  return !isApplyBlocked && !showDiscountRewardsOnly && !!isRedeemableOnline && isEligibleForReward && !stackingBlocked;
}

export const DposApplyRewardsCheckout = ({
  rewards = [],
  onApplyReward,
  onUndoReward,
  rewardsBalanceNumber,
  showDiscountRewardsOnly,
  maxRedemptionsPerTransaction,
  showRedemptionLimit,
  isApplyBlocked = showDiscountRewardsOnly,
}: RewardsProps): JSX.Element => {
  const Cart = useCart();
  const { appliedRewards = [] } = Cart;
  // Automatic sale/BOGO discounts or an applied promo code must not combine with a reward whose
  // POS discount is configured non-stacking (ECOMM-11514) — same signals the legacy rewards
  // wallet guards on (Cart.hasDiscounts / coupon).
  const hasCartDiscount = Cart.hasDiscounts || !!Cart.coupon;
  // The primary caller already filters null rewards; retain this guard for any future callers.
  const availableRewards = rewards.filter((reward): reward is RewardV2 => reward != null && reward.available);
  const appliedDposRewards = appliedRewards.filter(
    ({ brand, isDposLoyalty }) => isDposLoyalty ?? brand?.toLowerCase() === 'dutchieposloyalty'
  );
  const appliedRewardIds = new Set(appliedDposRewards.map(({ id }) => id));
  const limitReached = maxRedemptionsPerTransaction !== null && appliedRewardIds.size >= maxRedemptionsPerTransaction;
  // Include points committed by prior selections so the combined rewards cannot exceed the balance.
  const appliedPointsCost = appliedDposRewards.reduce((total, { id: appliedId }) => {
    const matched = availableRewards.find(({ id }) => id === appliedId);
    return total + (matched?.costV2 ?? 0);
  }, 0);

  const handleApplyReward = (
    copy: string,
    id: string,
    isRedeemableOnline: boolean | null | undefined,
    isEligibleForReward: boolean,
    allowStacking?: boolean | null
  ): void => {
    // Enforce eligibility in the handler rather than relying only on the button's disabled state.
    if (
      !canApplyReward({
        isApplyBlocked,
        showDiscountRewardsOnly,
        isRedeemableOnline,
        isEligibleForReward,
        allowStacking,
        hasCartDiscount,
      })
    ) {
      return;
    }
    onApplyReward?.(copy, id);
  };

  useEffect(() => {
    appliedRewards.forEach((appliedReward) => {
      const reward = rewards.find((r) => r.id === appliedReward.id);
      if (reward?.isEligible === false || (hasCartDiscount && reward?.allowStacking === false)) {
        onUndoReward?.(appliedReward.id);
      }
    });
  }, [appliedRewards, onUndoReward, rewards, hasCartDiscount]);
  return (
    <Container>
      {showDiscountRewardsOnly ? (
        <InStoreHeaderText className='fine-print'>
          <b>Redeemable in-store only.</b> Cannot be used online.
        </InStoreHeaderText>
      ) : (
        <>
          <HeaderText>The following reward options are eligible for this order</HeaderText>
          {showRedemptionLimit && maxRedemptionsPerTransaction !== null && (
            <RedemptionLimit>
              {maxRedemptionsPerTransaction} {maxRedemptionsPerTransaction === 1 ? 'reward' : 'rewards'} per order
            </RedemptionLimit>
          )}
        </>
      )}

      {availableRewards.length > 0 ? (
        availableRewards.map(({ copy, costV2, id, isRedeemableOnline, isEligible, allowStacking }) => {
          const isApplied = appliedRewardIds.has(id);
          const isEligibleForReward = isEligible !== false;
          const canApply = canApplyReward({
            isApplyBlocked,
            showDiscountRewardsOnly,
            isRedeemableOnline,
            isEligibleForReward,
            allowStacking,
            hasCartDiscount,
          });
          const isDisabled = (!isApplied && (limitReached || !canApply)) || showDiscountRewardsOnly;
          // Check this reward plus prior selections against the available point balance.
          const tooExpensive = isApplied ? false : costV2 + appliedPointsCost > rewardsBalanceNumber;

          return (
            <RewardCard
              key={`${id}-${copy ?? ''}`}
              isDisabled={isDisabled}
              hide={!isEligibleForReward}
              showDiscountRewardsOnly={showDiscountRewardsOnly}
            >
              <RewardInfo>
                <RewardTitle isDisabled={isDisabled || tooExpensive}>{copy}</RewardTitle>
                <PointsRow>
                  <PointsCost isDisabled={isDisabled || tooExpensive}>{costV2} pts</PointsCost>
                  {isApplied && (
                    <AppliedContainer>
                      <CheckIcon>✓</CheckIcon>
                      <AppliedText>Applied</AppliedText>
                    </AppliedContainer>
                  )}
                </PointsRow>
              </RewardInfo>
              {isApplied && (
                // Keep undo available if an applied reward later becomes ineligible.
                <Button onClick={() => onUndoReward?.(id)}>UNDO</Button>
              )}
              {!isApplied && canApply && (
                <Button
                  onClick={() =>
                    handleApplyReward(copy ?? '', id, isRedeemableOnline, isEligibleForReward, allowStacking)
                  }
                  disabled={isDisabled || tooExpensive}
                >
                  APPLY
                </Button>
              )}
            </RewardCard>
          );
        })
      ) : (
        <NoRewardsAvailable>No eligible rewards at this time</NoRewardsAvailable>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px 0;
  font-weight: 400;
`;

const RedemptionLimit = styled.p`
  font-size: 14px;
  color: var(--dcm-loyalty-redemption-limit-text-color, #6b7280);
  margin: -8px 0 8px;
  font-weight: 400;
`;

const InStoreHeaderText = styled.p`
  font-size: 16px;
  color: #6b7280;
  font-weight: 400;
`;

const RewardCard = styled.div<{ isDisabled?: boolean; hide?: boolean; showDiscountRewardsOnly?: boolean }>`
  display: ${({ hide }) => (hide ? 'none' : 'flex')};

  background-color: transparent;
  padding: ${({ showDiscountRewardsOnly }) => (showDiscountRewardsOnly ? '0' : '12px 0')};
  justify-content: space-between;
  align-items: center;
`;

const RewardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const RewardTitle = styled.h3<{ isDisabled?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ isDisabled }) => (isDisabled ? '#A0A7AB' : '#1f2937')};
  margin: 0;
  line-height: 1.25;
`;

const PointsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AppliedContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckIcon = styled.span`
  color: #10b981;
  font-weight: bold;
  font-size: 14px;
`;

const AppliedText = styled.span`
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
`;

const PointsCost = styled.p<{ isDisabled?: boolean }>`
  font-size: 14px;
  color: ${({ isDisabled }) => (isDisabled ? '#A0A7AB' : '#6b7280')};
  margin: 0;
  font-weight: 400;
`;

const NoRewardsAvailable = styled.div`
  color: ${colors.blueGrey};
  text-align: center;
  padding: 24px;
  font-style: italic;
`;
